/**
 * Copyright Warner Bros. Entertainment, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property
 * of Warner Bros. Entertainment, Inc. and its suppliers, if any.
 * The intellectual and technical concepts contained herein are
 * proprietary to Warner Bros. Entertainment, Inc. and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in process,
 * and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is
 * unlawful and strictly forbidden unless prior written permission is
 * obtained from Warner Bros. Entertainment, Inc.
 */
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {Button, Glyphicon, Modal} from 'react-bootstrap';

import style from './synopses.module.less';
import {DownloadActions} from '../../../common/download/download-actions';
import {MessagesContext} from '../../../messages/messages-context';
import {shouldHideSynopses, parseSynopses, TitleConstants} from '../../title-actions';

const {SYNOPSIS_TYPES} = TitleConstants;

class Synopses extends Component {
    static get propTypes() {
        return {
            close: PropTypes.func.isRequired,
            synopsesAreHidden: PropTypes.bool.isRequired,
            show: PropTypes.bool.isRequired,
            synopses: PropTypes.instanceOf(Immutable.List),
            synopsisUsageType: PropTypes.string,
            title: PropTypes.instanceOf(Immutable.Map).isRequired
        };
    }

    static get defaultProps() {
        return {
            synopses: Immutable.List(),
            synopsisUsageType: undefined
        };
    }

    constructor(props) {
        super(props);
        this.getValidSynopses = this.getValidSynopses.bind(this);
        this.handleExportMultipleSynopsis = this.handleExportMultipleSynopsis.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onDone = this.onDone.bind(this);
    }

    static contextType = MessagesContext;

    handleExportMultipleSynopsis() {
        const {title} = this.props;
        DownloadActions.startDownloadExecution(`title/${title.get('id')}/synopsis/export-rtf`);
    }
    getValidSynopses() {
        const {synopsesAreHidden, synopses: alternativeSynopses, synopsisUsageType, title} = this.props;
        const hasDisplayableSynopsis = (source) => !shouldHideSynopses(source);

        const allSynopses = [];

        if (synopsesAreHidden) {
            const screenerSynopses = alternativeSynopses.find(s =>
                s.get('usageType') === SYNOPSIS_TYPES.SCREENER.id &&
                synopsisUsageType === SYNOPSIS_TYPES.SCREENER.id
            );
            if (screenerSynopses) {
                allSynopses.push(screenerSynopses);
            }
        } else {
            // add default synopses from title first (not included in GET /synopsis response)
            allSynopses.push(title);

            alternativeSynopses.forEach(s => {
                const isScreenerSynopses = s.get('usageType') === SYNOPSIS_TYPES.SCREENER.id;
                // hide screener synopses if SCREENER is not the selected usage type
                if (isScreenerSynopses && s.get('usageType') !== synopsisUsageType) {
                    return;
                }
                allSynopses.push(s);
            });
        }

        const validSynopses = allSynopses
            .map(synopses => parseSynopses(synopses))
            .filter(parsedSynopses => hasDisplayableSynopsis(Immutable.fromJS(parsedSynopses)));
        return validSynopses;
    }

    onClose() {
        this.props.close();
    }

    onDone() {
        this.onClose();
    }

    render() {
        const {intl} = this.context;
        const {show} = this.props;

        const validSynopses = this.getValidSynopses();

        let bodyContent;
        if (validSynopses.length === 0) {
            bodyContent = (
                <ul className={style.synopses} key="synopses-not-available">
                    <li><strong>{intl.messages['title.synopsis.not-available']}</strong></li>
                </ul>
            );
        } else {
            bodyContent = validSynopses.map((s = {}, key) => {
                let synopsis, short, s180, s60;
                if (s.synopsis) {
                    synopsis = <li>
                        <p dangerouslySetInnerHTML={{__html: s.synopsis}} />
                    </li>;
                }
                if (s.shortSynopsis) {
                    short = <li>
                        <p><u>{intl.messages['title.modal.synopses.short']}</u></p>
                        <p dangerouslySetInnerHTML={{__html: s.shortSynopsis}} />
                    </li>;
                }
                if (s.log180Lines) {
                    s180 = <li>
                        <p><u>{intl.messages['title.modal.synopses.log180']}</u></p>
                        <p dangerouslySetInnerHTML={{__html: s.log180Lines}} />
                    </li>;
                }
                if (s.log60Lines) {
                    s60 = <li>
                        <p><u>{intl.messages['title.modal.synopses.log60']}</u></p>
                        <p dangerouslySetInnerHTML={{__html: s.log60Lines}} />
                    </li>;
                }
                return (
                    <ul className={style.synopses} key={`${SYNOPSIS_TYPES[s.usageType].id}-${key}`}>
                        <li>{SYNOPSIS_TYPES[s.usageType].name}</li>
                        {synopsis}
                        {short}
                        {s180}
                        {s60}
                    </ul>
                );
            });
        }

        return (
            <Modal show={show} onHide={this.onClose}>
                <Modal.Header className="bg-gray" closeButton>
                    <Modal.Title className="modal-title text-center text-uppercase">{intl.messages['title.modal.synopses.title']}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h1 className="h3">{intl.messages['title.modal.synopses.subtitle']}</h1>
                    <em>{intl.messages['title.modal.synopses.text']}</em>
                    <div>
                        {bodyContent}
                    </div>
                </Modal.Body>
                <Modal.Footer className="padding-all-20">
                    <Button
                        bsStyle="default"
                        className="pull-left btn-secondary"
                        onClick={this.onClose}>{intl.messages['common.close']}
                    </Button>
                    <Button
                        bsStyle="primary"
                        className="pull-right"
                        onClick={this.handleExportMultipleSynopsis}>
                        <Glyphicon glyph="download-alt" /><span>&nbsp;{intl.messages['common.download']}</span>
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default Synopses;
export {Synopses};
